import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  Link,
  LinearProgress,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SaveIcon from '@mui/icons-material/Save';
import SyncIcon from '@mui/icons-material/Sync';
import TennisRecordAutomationPilot from './TennisRecordAutomationPilot';
import type { LookupState } from '../types';
import { supabase } from '../config/supabase';
import { useAuditUserId } from '../auth/AuthContext';
import { usePersistentScrollRestoration, usePersistentViewState } from '../hooks/usePersistentViewState';

type SyncSettingRow = {
  workspace_id: string;
  scope: 'all_active_players' | 'active_rosters' | 'active_rosters_active_seasons';
  minimum_sync_age_hours: number;
  delay_seconds: number;
  tennis_record_request_timeout_seconds: number;
  execution_budget_seconds: number;
  transient_retry_delay_minutes: number;
  last_run_started_at: string | null;
  last_run_completed_at: string | null;
  last_run_status: string | null;
};

type ScheduledRunRow = {
  id: string;
  workspace_id: string;
  trigger_source: 'schedule' | 'manual';
  status: string;
  started_at: string;
  completed_at: string | null;
  players_checked: number;
  players_changed: number;
  players_failed: number;
  players_skipped: number;
  players_queued?: number;
  players_pending?: number;
  players_processing?: number;
  opponent_matches_checked?: number;
  opponent_matches_changed?: number;
  opponent_matches_failed?: number;
  opponent_matches_skipped?: number;
  error_message: string | null;
};

type Snack = { severity: 'success' | 'error'; message: string };

type SyncRunSummary = {
  runId?: string | null;
  workspaceId?: string;
  status?: string;
  checked?: number;
  changed?: number;
  failed?: number;
  skipped?: number;
  recentSkipped?: number;
  retrySkipped?: number;
  deferred?: number;
  queued?: number;
  pending?: number;
  processing?: number;
  opponentChecked?: number;
  opponentChanged?: number;
  opponentFailed?: number;
  opponentSkipped?: number;
  opponentRecentSkipped?: number;
  opponentRetrySkipped?: number;
  opponentDeferred?: number;
  staleRunsCleared?: number;
  reason?: string;
  error?: string;
  playerResults?: ManualPlayerSyncResult[];
};

type ManualSyncPlanPlayer = {
  playerId: string;
  playerName: string;
  previousEdr?: number | null;
  url?: string | null;
};

type ManualSyncPlan = SyncRunSummary & {
  players?: ManualSyncPlanPlayer[];
  requestTimeoutSeconds?: number;
  delaySeconds?: number;
  executionBudgetSeconds?: number;
};

type ManualPlayerSyncResult = {
  playerId?: string;
  playerName?: string;
  startedAt?: string | null;
  status: 'success' | 'failed' | 'skipped' | 'deferred' | string;
  reason?: string | null;
  previousEdr?: number | null;
  newEdr?: number | null;
  changed?: boolean;
  url?: string | null;
  error?: string | null;
  tennisRecordDisplayName?: string | null;
  lastSuccessfulSyncAt?: string | null;
};

type TennisRecordSyncRunRow = {
  id: string;
  player_id: string | null;
  url: string;
  started_at: string;
  completed_at: string | null;
  status: string;
  prior_value: number | null;
  observed_value: number | null;
  changed: boolean;
  error_message: string | null;
  players?: {
    first_name?: string | null;
    last_name?: string | null;
    tennis_record_display_name?: string | null;
    tennis_record_last_successful_sync_at?: string | null;
  } | null;
};

function defaultSetting(workspaceId: string): SyncSettingRow {
  return {
    workspace_id: workspaceId,
    scope: 'all_active_players',
    minimum_sync_age_hours: 24,
    delay_seconds: 15,
    tennis_record_request_timeout_seconds: 10,
    execution_budget_seconds: 120,
    transient_retry_delay_minutes: 120,
    last_run_started_at: null,
    last_run_completed_at: null,
    last_run_status: null,
  };
}

function formatDateTime(value: string | null): string {
  if (!value) return '-';
  return new Date(value).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' });
}

function statusColor(status: string): 'success' | 'warning' | 'error' | 'default' {
  if (status === 'success') return 'success';
  if (status === 'completed_with_errors' || status === 'skipped' || status === 'running' || status === 'partial' || status === 'deferred') return 'warning';
  if (status === 'failed') return 'error';
  return 'default';
}

function formatEdrValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return '-';
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric.toFixed(2) : '-';
}

function numberValue(value: unknown): number {
  const numeric = Number(value || 0);
  return Number.isFinite(numeric) ? numeric : 0;
}

function runSummaryMessage(summary?: SyncRunSummary): string {
  if (!summary) return 'TennisRecord sync run completed.';
  const checked = numberValue(summary.checked);
  const queued = numberValue(summary.queued);
  const pending = numberValue(summary.pending);
  const processing = numberValue(summary.processing);
  const opponentChecked = numberValue(summary.opponentChecked);
  const changed = numberValue(summary.changed) + numberValue(summary.opponentChanged);
  const failed = numberValue(summary.failed) + numberValue(summary.opponentFailed);
  const recent = numberValue(summary.recentSkipped) + numberValue(summary.opponentRecentSkipped);
  const retry = numberValue(summary.retrySkipped) + numberValue(summary.opponentRetrySkipped);
  const deferred = numberValue(summary.deferred) + numberValue(summary.opponentDeferred);
  const staleCleared = numberValue(summary.staleRunsCleared);
  const skipped = numberValue(summary.skipped) + numberValue(summary.opponentSkipped);
  const details = [
    `${checked} players checked`,
    queued ? `${queued} jobs queued` : '',
    pending ? `${pending} pending` : '',
    processing ? `${processing} processing` : '',
    `${opponentChecked} opponents checked`,
    `${changed} changed`,
    `${failed} failed`,
    `${skipped} skipped total`,
    recent ? `${recent} skipped recently` : '',
    retry ? `${retry} waiting for retry window` : '',
    deferred ? `${deferred} deferred by run budget` : '',
    staleCleared ? `${staleCleared} old running run${staleCleared === 1 ? '' : 's'} cleared` : '',
    summary.reason ? `reason: ${String(summary.reason).replace(/_/g, ' ')}` : '',
  ].filter(Boolean);
  return `Sync ${summary.status ? summary.status.replace(/_/g, ' ') : 'complete'}: ${details.join(', ')}.`;
}

function staleRunCutoff(setting: SyncSettingRow): string {
  const budgetMs = Math.max((Number(setting.execution_budget_seconds) || 120) * 1000 + 15000, 60000);
  return new Date(Date.now() - budgetMs).toISOString();
}

function syncRunReason(row: TennisRecordSyncRunRow): string {
  if (row.status === 'success') return row.changed ? 'updated' : 'unchanged';
  if (row.status === 'failed') return row.error_message?.includes('configured run budget') ? 'run_budget' : 'error';
  if (row.status === 'skipped' && row.error_message) return row.error_message;
  return row.status;
}

function manualResultFromSyncRun(row: TennisRecordSyncRunRow): ManualPlayerSyncResult {
  const playerName = `${row.players?.first_name || ''} ${row.players?.last_name || ''}`.trim();
  return {
    playerId: row.player_id || undefined,
    playerName: playerName || 'Unknown player',
    startedAt: row.completed_at || row.started_at,
    status: row.status,
    reason: syncRunReason(row),
    previousEdr: row.prior_value,
    newEdr: row.observed_value,
    changed: row.changed,
    url: row.url,
    error: row.error_message,
    tennisRecordDisplayName: row.players?.tennis_record_display_name || null,
    lastSuccessfulSyncAt: row.players?.tennis_record_last_successful_sync_at || null,
  };
}

function actionLabel(row: ManualPlayerSyncResult): string {
  const value = String(row.reason || row.status || '').replace(/_/g, ' ').trim();
  if (!value) return '-';
  if (/^skipped$/i.test(value) && row.error) return row.error;
  if (/^retry window$/i.test(value)) return 'Waiting for retry window';
  if (/^recently synced$/i.test(value)) return 'Skipped: recently synced';
  return value;
}

function displayNewEdr(row: ManualPlayerSyncResult): string {
  if ((row.newEdr === null || row.newEdr === undefined) && row.changed === false) return formatEdrValue(row.previousEdr);
  return formatEdrValue(row.newEdr);
}

function comparableSetting(setting: SyncSettingRow | null) {
  if (!setting) return null;
  return {
    minimum_sync_age_hours: Number(setting.minimum_sync_age_hours) || 24,
    delay_seconds: Number(setting.delay_seconds) || 0,
    tennis_record_request_timeout_seconds: Number(setting.tennis_record_request_timeout_seconds) || 10,
    execution_budget_seconds: Number(setting.execution_budget_seconds) || 120,
    transient_retry_delay_minutes: Number(setting.transient_retry_delay_minutes) || 120,
  };
}

const MANUAL_RUN_NOW_MAX_PLAYERS = import.meta.env.COURTCAPTAIN_ENV === 'staging' ? 3 : 500;

export function TennisRecordSyncSettings({ lookups, isAdmin }: { lookups: LookupState; isAdmin: boolean }) {
  const auditUserId = useAuditUserId();
  const {
    state: savedViewState,
    setState: setSavedViewState,
    stateKey: viewStateKey,
    loadedStateKey,
  } = usePersistentViewState({
    userId: auditUserId,
    routeKey: 'tennis-record-sync-settings',
    defaultState: { selectedWorkspaceId: '' },
  });
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState(savedViewState.selectedWorkspaceId);
  const [setting, setSetting] = useState<SyncSettingRow | null>(null);
  const [savedSetting, setSavedSetting] = useState<SyncSettingRow | null>(null);
  const [runs, setRuns] = useState<ScheduledRunRow[]>([]);
  const [scheduledEnabled, setScheduledEnabled] = useState<boolean | null>(null);
  const [lastScheduledRun, setLastScheduledRun] = useState<ScheduledRunRow | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [manualForceRefresh, setManualForceRefresh] = useState(false);
  const [manualMaxPlayers] = useState(MANUAL_RUN_NOW_MAX_PLAYERS);
  const [edrActivityLog, setEdrActivityLog] = useState<ManualPlayerSyncResult[]>([]);
  const [snack, setSnack] = useState<Snack | null>(null);
  const restoredViewStateKey = useRef('');
  const [viewStateReady, setViewStateReady] = useState(false);

  usePersistentScrollRestoration({ userId: auditUserId, routeKey: 'tennis-record-sync-settings', ready: !loading && !saving && !running });

  useEffect(() => {
    if (!auditUserId || loadedStateKey !== viewStateKey) {
      setViewStateReady(false);
      return;
    }
    if (restoredViewStateKey.current === viewStateKey) {
      setViewStateReady(true);
      return;
    }
    setSelectedWorkspaceId(savedViewState.selectedWorkspaceId);
    restoredViewStateKey.current = viewStateKey;
    setViewStateReady(true);
  }, [auditUserId, loadedStateKey, savedViewState, viewStateKey]);

  useEffect(() => {
    if (!viewStateReady || restoredViewStateKey.current !== viewStateKey) return;
    setSavedViewState({ selectedWorkspaceId });
  }, [selectedWorkspaceId, setSavedViewState, viewStateKey, viewStateReady]);

  useEffect(() => {
    if (!selectedWorkspaceId && lookups.workspaces.length > 0) setSelectedWorkspaceId(lookups.workspaces[0].value);
  }, [lookups.workspaces, selectedWorkspaceId]);

  const loadData = useCallback(async () => {
    if (!selectedWorkspaceId) return;
    setLoading(true);
    try {
      const session = (await supabase.auth.getSession()).data.session;
      if (session) {
        const response = await fetch('/api/tennisrecord/scheduler', { headers: { Authorization: `Bearer ${session.access_token}` } });
        setScheduledEnabled(response.ok ? (await response.json()).enabled === true : null);
      }
    } catch { setScheduledEnabled(null); }
    const scheduledResult = await supabase.from('tennis_record_scheduled_runs').select('*')
      .eq('workspace_id', selectedWorkspaceId).eq('trigger_source', 'schedule')
      .not('scheduler_day', 'is', null).order('started_at', { ascending: false }).limit(1);
    setLastScheduledRun((scheduledResult.data?.[0] as ScheduledRunRow | undefined) || null);
    const settingsResult = await supabase
      .from('tennis_record_sync_settings')
      .select('*')
      .eq('workspace_id', selectedWorkspaceId)
      .maybeSingle();
    if (settingsResult.error && settingsResult.error.code !== 'PGRST116') {
      setLoading(false);
      setSnack({ severity: 'error', message: settingsResult.error.message });
      setSetting(defaultSetting(selectedWorkspaceId));
      return;
    }
    const nextSetting = settingsResult.data ? {
      ...(settingsResult.data as SyncSettingRow),
      tennis_record_request_timeout_seconds: Number((settingsResult.data as SyncSettingRow).tennis_record_request_timeout_seconds) || 10,
      execution_budget_seconds: Number((settingsResult.data as SyncSettingRow).execution_budget_seconds) || 120,
      transient_retry_delay_minutes: Number((settingsResult.data as SyncSettingRow).transient_retry_delay_minutes) || 120,
    } : defaultSetting(selectedWorkspaceId);

    const cutoff = staleRunCutoff(nextSetting);
    const staleResult = await supabase
      .from('tennis_record_scheduled_runs')
      .update({
        completed_at: new Date().toISOString(),
        status: 'failed',
        error_message: 'Run was marked failed by the TennisRecord Sync screen because it exceeded the configured run budget.',
      })
      .eq('workspace_id', selectedWorkspaceId)
      .eq('status', 'running')
      .is('scheduler_environment', null)
      .lt('started_at', cutoff)
      .select('id');
    if (!staleResult.error && (staleResult.data || []).length > 0) {
      await supabase
        .from('tennis_record_sync_runs')
        .update({
          completed_at: new Date().toISOString(),
          status: 'failed',
          error_message: 'Player sync was marked failed because the parent manual run exceeded the configured run budget.',
        })
        .eq('sync_type', 'player_profile')
        .is('completed_at', null)
        .lt('started_at', cutoff);
      await supabase
        .from('tennis_record_sync_settings')
        .update({
          last_run_completed_at: new Date().toISOString(),
          last_run_status: 'failed',
          updated_at: new Date().toISOString(),
        })
        .eq('workspace_id', selectedWorkspaceId);
      setRunning(false);
      setSnack({
        severity: 'error',
        message: `${staleResult.data?.length || 0} over-budget TennisRecord run${(staleResult.data?.length || 0) === 1 ? '' : 's'} marked failed.`,
      });
    }

    const runsResult = await supabase
      .from('tennis_record_scheduled_runs')
      .select('id, workspace_id, trigger_source, status, started_at, completed_at, players_checked, players_changed, players_failed, players_skipped, players_queued, players_pending, players_processing, opponent_matches_checked, opponent_matches_changed, opponent_matches_failed, opponent_matches_skipped, error_message')
      .eq('workspace_id', selectedWorkspaceId)
      .order('started_at', { ascending: false })
      .limit(20);
    setLoading(false);
    if (runsResult.error) {
      setSnack({ severity: 'error', message: runsResult.error.message });
    }
    const runRows = (runsResult.data || []) as ScheduledRunRow[];
    const activityCutoff = new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString();
    const activityRunsResult = await supabase
      .from('tennis_record_scheduled_runs')
      .select('started_at, completed_at')
      .eq('workspace_id', selectedWorkspaceId)
      .gte('started_at', activityCutoff)
      .order('started_at', { ascending: false })
      .limit(300);
    const playerHistoryResult = await supabase
      .from('tennis_record_sync_runs')
      .select('id, player_id, url, started_at, completed_at, status, prior_value, observed_value, changed, error_message, players(first_name, last_name, tennis_record_display_name, tennis_record_last_successful_sync_at)')
      .eq('sync_type', 'player_profile')
      .gte('started_at', activityCutoff)
      .order('started_at', { ascending: false })
      .limit(500);
    if (!activityRunsResult.error && !playerHistoryResult.error) {
      const nowTime = Date.now();
      const runWindows = ((activityRunsResult.data || []) as Pick<ScheduledRunRow, 'started_at' | 'completed_at'>[]).map((run) => ({
        started: new Date(run.started_at).getTime(),
        completed: new Date(run.completed_at || new Date(nowTime + 60 * 1000).toISOString()).getTime(),
      }));
      const activityRows = ((playerHistoryResult.data || []) as TennisRecordSyncRunRow[]).filter((row) => {
        const rowTime = new Date(row.started_at).getTime();
        return runWindows.some((window) => rowTime >= window.started && rowTime <= window.completed);
      });
      setEdrActivityLog(activityRows.map(manualResultFromSyncRun));
    }
    setSetting(nextSetting);
    setSavedSetting(settingsResult.data ? nextSetting : null);
    setRuns(runRows);
  }, [selectedWorkspaceId]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  useEffect(() => {
    if (!running && !runs.some((run) => run.status === 'running')) return undefined;
    const timer = window.setInterval(() => {
      void loadData();
    }, 15000);
    return () => window.clearInterval(timer);
  }, [loadData, running, runs]);

  const updateSetting = (patch: Partial<SyncSettingRow>) => {
    setSetting((current) => current ? { ...current, ...patch } : current);
  };

  const saveSetting = async () => {
    if (!setting || !isAdmin) return;
    setSaving(true);
    const syncSettingsChanged = !savedSetting
      || Number(savedSetting.tennis_record_request_timeout_seconds) !== Number(setting.tennis_record_request_timeout_seconds)
      || Number(savedSetting.execution_budget_seconds) !== Number(setting.execution_budget_seconds)
      || Number(savedSetting.transient_retry_delay_minutes) !== Number(setting.transient_retry_delay_minutes)
      || Number(savedSetting.minimum_sync_age_hours) !== Number(setting.minimum_sync_age_hours)
      || Number(savedSetting.delay_seconds) !== Number(setting.delay_seconds);
    const payload = {
      workspace_id: setting.workspace_id,
      scope: 'all_active_players',
      minimum_sync_age_hours: Number(setting.minimum_sync_age_hours) || 24,
      delay_seconds: Number(setting.delay_seconds) || 0,
      tennis_record_request_timeout_seconds: Number(setting.tennis_record_request_timeout_seconds) || 10,
      execution_budget_seconds: Number(setting.execution_budget_seconds) || 120,
      transient_retry_delay_minutes: Number(setting.transient_retry_delay_minutes) || 120,
      updated_at: new Date().toISOString(),
    };
    const { error } = await supabase.from('tennis_record_sync_settings').upsert(payload, { onConflict: 'workspace_id' });
    setSaving(false);
    if (error) {
      setSnack({ severity: 'error', message: error.message });
      return;
    }
    setSnack({ severity: 'success', message: syncSettingsChanged ? 'TennisRecord worker sync settings saved.' : 'TennisRecord worker sync settings saved.' });
    await loadData();
  };

  const runNow = async () => {
    if (!setting || !isAdmin) return;
    setRunning(true);
    const session = (await supabase.auth.getSession()).data.session;
    const authHeaders = {
      'Content-Type': 'application/json',
      ...(session ? { Authorization: `Bearer ${session.access_token}` } : {}),
    };
    const response = await fetch('/api/tennisrecord/scheduled-sync', {
      method: 'POST',
      headers: authHeaders,
      body: JSON.stringify({
        workspaceId: setting.workspace_id,
        ignoreSchedule: true,
        triggerSource: 'manual',
        force: manualForceRefresh,
        maxPlayers: manualMaxPlayers,
      }),
    });
    const result = await response.json() as { error?: string; plans?: ManualSyncPlan[] };
    setRunning(false);
    if (!response.ok) {
      setSnack({ severity: 'error', message: result.error || 'Unable to run TennisRecord sync.' });
      return;
    }
    const plan = result.plans?.[0];
    if (!plan) {
      setSnack({ severity: 'error', message: 'Unable to build TennisRecord manual sync plan.' });
      return;
    }
    if (plan.status === 'skipped') {
      setSnack({ severity: 'error', message: runSummaryMessage(plan) });
      await loadData();
      return;
    }

    if (plan.playerResults?.length) {
      const now = new Date().toISOString();
      setEdrActivityLog((current) => [
        ...plan.playerResults!.map((row) => ({ ...row, startedAt: row.startedAt || now })),
        ...current,
      ]);
    }
    setSnack({
      severity: numberValue(plan.queued) > 0 ? 'success' : 'error',
      message: numberValue(plan.queued) > 0
        ? `TennisRecord sync started. ${numberValue(plan.queued)} player refresh job${numberValue(plan.queued) === 1 ? '' : 's'} queued. You can leave this page.`
        : runSummaryMessage(plan),
    });
    await loadData();
  };

  const hasUnsavedSettings = JSON.stringify(comparableSetting(setting)) !== JSON.stringify(comparableSetting(savedSetting));

  useEffect(() => {
    if (!setting || !isAdmin || loading) return;
    if (window.sessionStorage.getItem('courtcaptain:run-tennisrecord-sync') !== '1') return;
    window.sessionStorage.removeItem('courtcaptain:run-tennisrecord-sync');
    if (running || saving) {
      setSnack({ severity: 'error', message: 'TennisRecord sync is already busy. Try again after the current action finishes.' });
      return;
    }
    if (hasUnsavedSettings) {
      setSnack({ severity: 'error', message: 'Save TennisRecord sync settings before starting from Command Center.' });
      return;
    }
    void runNow();
  }, [hasUnsavedSettings, isAdmin, loading, running, saving, setting]);

  if (!isAdmin) return <Alert severity="warning">TennisRecord Sync Settings are available only to administrators.</Alert>;
  if (!setting) return <LinearProgress />;

  return (
    <Stack spacing={2.5}>
      <TennisRecordAutomationPilot />
      <Box sx={{ borderBottom: 1, borderColor: 'divider', pb: 2 }}>
        <Typography variant="h6">Scheduled Refreshes</Typography>
        <Typography variant="body2">Status: {scheduledEnabled === null ? 'Unavailable' : scheduledEnabled ? 'Enabled' : 'Disabled'} | Daily | 08:00 UTC (02:00 CST / 03:00 CDT)</Typography>
        <Typography variant="body2">Last scheduled run: {lastScheduledRun ? new Date(lastScheduledRun.started_at).toLocaleString() : 'None'}</Typography>
        {lastScheduledRun && <Typography variant="body2">{lastScheduledRun.status}: checked {lastScheduledRun.players_checked}, changed {lastScheduledRun.players_changed}, failed {lastScheduledRun.players_failed}, skipped {lastScheduledRun.players_skipped}</Typography>}
      </Box>
      <Box>
        <Typography variant="h5" sx={{ fontWeight: 800 }}>TennisRecord Sync Settings</Typography>
        <Typography variant="body2" color="text.secondary">
          Queue player EDR refreshes for the automation worker and monitor durable progress.
        </Typography>
      </Box>

      <Paper variant="outlined" sx={{ p: 2 }}>
        {loading && <LinearProgress sx={{ mb: 2 }} />}
        <Stack spacing={2}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 2 }}>
            <TextField label="Skip if synced within hours" type="number" value={setting.minimum_sync_age_hours} onChange={(event) => updateSetting({ minimum_sync_age_hours: Number(event.target.value) })} />
            <TextField
              label="Active run recovery window (seconds)"
              type="number"
              value={setting.execution_budget_seconds}
              helperText="When to mark an unfinished queueing run over budget."
              inputProps={{ min: 20, max: 840 }}
              onChange={(event) => updateSetting({ execution_budget_seconds: Number(event.target.value) })}
            />
          </Box>

          <Alert severity="info">
            Run Now queues active players with TennisRecord URLs, skips recently successful refreshes unless force refresh is selected, and lets the permanent worker process one player at a time. You can leave this page after jobs are queued.
          </Alert>

          <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'action.hover' }}>
            <Stack spacing={1.5}>
              <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Manual Run Now Options</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr' }, gap: 2, alignItems: 'center' }}>
                <FormControlLabel
                  control={<Checkbox checked={manualForceRefresh} onChange={(event) => setManualForceRefresh(event.target.checked)} />}
                  label="Force refresh recently synced players"
                />
              </Box>
              <Typography variant="body2" color="text.secondary">
                Run Now will queue all eligible players for worker-backed refresh, up to the safety cap of {manualMaxPlayers}.
              </Typography>
            </Stack>
          </Paper>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Last run: {setting.last_run_completed_at ? `${formatDateTime(setting.last_run_completed_at)} (${setting.last_run_status || 'unknown'})` : 'Never'}
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button variant="outlined" startIcon={<PlayArrowIcon />} disabled={running || saving || hasUnsavedSettings} onClick={() => void runNow()}>
                {running ? 'Running...' : 'Sync All Players'}
              </Button>
              <Button variant="contained" startIcon={<SaveIcon />} disabled={saving || running} onClick={() => void saveSetting()}>
                {saving ? 'Saving...' : 'Save Settings'}
              </Button>
            </Stack>
          </Stack>
          {hasUnsavedSettings && (
            <Typography variant="caption" color="text.secondary" sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
              Save settings before running a manual sync so the run uses the displayed values.
            </Typography>
          )}
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
          <SyncIcon color="primary" />
          <Typography variant="h6" sx={{ fontWeight: 800 }}>EDR Activity Log</Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Player-level TennisRecord sync results from worker-backed runs over the last 90 days.
        </Typography>
        <Box sx={{ overflow: 'auto', maxHeight: 420 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>When</TableCell>
                <TableCell>Player</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Action</TableCell>
                <TableCell align="right">Previous EDR</TableCell>
                <TableCell align="right">New EDR</TableCell>
                <TableCell>Changed</TableCell>
                <TableCell>TennisRecord Name</TableCell>
                <TableCell>Last Refreshed</TableCell>
                <TableCell>URL</TableCell>
                <TableCell>Error</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {edrActivityLog.map((row, index) => (
                <TableRow key={`${row.playerId || row.playerName || 'player'}-${row.startedAt || index}-${index}`}>
                  <TableCell>{formatDateTime(row.startedAt || null)}</TableCell>
                  <TableCell>{row.playerName || 'Unknown player'}</TableCell>
                  <TableCell>
                    <Chip size="small" color={statusColor(row.status)} label={row.status.replace(/_/g, ' ')} />
                  </TableCell>
                  <TableCell>{actionLabel(row)}</TableCell>
                  <TableCell align="right">{formatEdrValue(row.previousEdr)}</TableCell>
                  <TableCell align="right">{displayNewEdr(row)}</TableCell>
                  <TableCell>{row.changed ? 'Yes' : 'No'}</TableCell>
                  <TableCell>{row.tennisRecordDisplayName || '-'}</TableCell>
                  <TableCell>{formatDateTime(row.lastSuccessfulSyncAt || null)}</TableCell>
                  <TableCell>{row.url ? <Link href={row.url} target="_blank" rel="noopener noreferrer">Open</Link> : '-'}</TableCell>
                  <TableCell>{row.error || '-'}</TableCell>
                </TableRow>
              ))}
              {edrActivityLog.length === 0 && (
                <TableRow>
                  <TableCell colSpan={14}>
                    <Typography variant="body2" color="text.secondary">No player EDR activity recorded in the last 90 days.</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
          <SyncIcon color="primary" />
          <Typography variant="h6" sx={{ fontWeight: 800 }}>Recent Sync Runs</Typography>
        </Stack>
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Started</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Trigger</TableCell>
                <TableCell align="right">Checked</TableCell>
                <TableCell align="right">Queued</TableCell>
                <TableCell align="right">Pending</TableCell>
                <TableCell align="right">Processing</TableCell>
                <TableCell align="right">Changed</TableCell>
                <TableCell align="right">Failed</TableCell>
                <TableCell align="right">Skipped</TableCell>
                <TableCell align="right">Opp Checked</TableCell>
                <TableCell align="right">Opp Changed</TableCell>
                <TableCell align="right">Opp Failed</TableCell>
                <TableCell>Error</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {runs.map((run) => (
                <TableRow key={run.id}>
                  <TableCell>{formatDateTime(run.started_at)}</TableCell>
                  <TableCell><Chip size="small" color={statusColor(run.status)} label={run.status.replace(/_/g, ' ')} /></TableCell>
                  <TableCell>{run.trigger_source}</TableCell>
                  <TableCell align="right">{run.players_checked}</TableCell>
                  <TableCell align="right">{run.players_queued || 0}</TableCell>
                  <TableCell align="right">{run.players_pending || 0}</TableCell>
                  <TableCell align="right">{run.players_processing || 0}</TableCell>
                  <TableCell align="right">{run.players_changed}</TableCell>
                  <TableCell align="right">{run.players_failed}</TableCell>
                  <TableCell align="right">{run.players_skipped}</TableCell>
                  <TableCell align="right">{run.opponent_matches_checked || 0}</TableCell>
                  <TableCell align="right">{run.opponent_matches_changed || 0}</TableCell>
                  <TableCell align="right">{run.opponent_matches_failed || 0}</TableCell>
                  <TableCell>{run.error_message || '-'}</TableCell>
                </TableRow>
              ))}
              {runs.length === 0 && (
                <TableRow>
                  <TableCell colSpan={11}>
                    <Typography variant="body2" color="text.secondary">No TennisRecord worker sync runs yet.</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      </Paper>

      <Snackbar open={Boolean(snack)} autoHideDuration={5000} onClose={() => setSnack(null)}>
        {snack ? <Alert severity={snack.severity} onClose={() => setSnack(null)}>{snack.message}</Alert> : undefined}
      </Snackbar>
    </Stack>
  );
}
